import React, { useState, useEffect } from 'react';
import { Search, Send, Check, X, ArrowRight, Download, RefreshCw, User, BookOpen, Clock, MapPin, CheckCircle2, AlertCircle, Clock3, Layers, UserCheck, Repeat } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const KardexCambioCarrera = () => {
    const [activeTab, setActiveTab] = useState('carrera'); // carrera, mencion, paralelo
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [gestion, setGestion] = useState(new Date().getFullYear());

    // Data states
    const [carreraSol, setCarreraSol] = useState([]);
    const [mencionSol, setMencionSol] = useState([]);
    const [paraleloSol, setParaleloSol] = useState([]);

    // Form/Modal states
    const [showModal, setShowModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [estudiantes, setEstudiantes] = useState([]);
    const [carreras, setCarreras] = useState([]);
    const [menciones, setMenciones] = useState([]);
    const [selectedEstudiante, setSelectedEstudiante] = useState(null);
    const [availableParalelos, setAvailableParalelos] = useState([]);

    const [formData, setFormData] = useState({
        id_carrera_destino: '',
        id_mencion_destino: '',
        id_asignacion_actual: '',
        id_asignacion_nueva: '',
        motivo: '',
        tipo_solicitante: 'kardex'
    });

    useEffect(() => {
        loadDashboardData();
    }, [activeTab, gestion]);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };
            const query = `?gestion=${gestion}`;

            if (activeTab === 'carrera') {
                const res = await fetch(`${API_URL}/kardex/cambio-carrera/solicitudes${query}`, { headers });
                const data = await res.json();
                setCarreraSol(data.solicitudes || []);
            } else if (activeTab === 'mencion') {
                const res = await fetch(`${API_URL}/kardex/cambio-mencion/solicitudes${query}`, { headers });
                const data = await res.json();
                setMencionSol(data.solicitudes || []);
            } else if (activeTab === 'paralelo') {
                const res = await fetch(`${API_URL}/kardex/cambio-paralelo/solicitudes${query}`, { headers });
                const data = await res.json();
                setParaleloSol(data.solicitudes || []);
            }
        } catch (error) {
            console.error('Error loading dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Dynamic search effect
    useEffect(() => {
        if (!showModal) return;

        const timeoutId = setTimeout(() => {
            if (search.length >= 3 || search.length === 0) {
                fetchSearchResults(search);
            }
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [search, activeTab, showModal]);

    const fetchSearchResults = async (query) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            let url = '';
            if (activeTab === 'paralelo') {
                url = `${API_URL}/kardex/cambio-paralelo/estudiantes?gestion=${gestion}&search=${query}`;
            } else {
                url = `${API_URL}/kardex/cambio-carrera/estudiantes?q=${query}`;
            }

            const res = await fetch(url, { headers });
            if (!res.ok) throw new Error(`Server returned ${res.status}`);

            const data = await res.json();
            setEstudiantes(data.estudiantes || []);
        } catch (error) {
            console.error('Error fetching search results:', error);
            setEstudiantes([]);
        }
    };

    const loadFormData = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            if (activeTab === 'carrera' || activeTab === 'mencion') {
                const [carRes, menRes] = await Promise.all([
                    fetch(`${API_URL}/kardex/carreras`, { headers }),
                    fetch(`${API_URL}/kardex/menciones`, { headers })
                ]);

                if (carRes.ok && menRes.ok) {
                    const carData = await carRes.json();
                    const menData = await menRes.json();
                    setCarreras(carData.carreras || []);
                    setMenciones(menData.menciones || []);
                } else {
                    console.error('Error fetching careers/mentions lists');
                }
            }
            // Initial load without query
            fetchSearchResults('');
        } catch (error) {
            console.error('Error loading form data:', error);
        }
    };

    const handleNewRequest = () => {
        setSearch(''); // Clear search to see all students in modal
        loadFormData();
        setShowModal(true);
        setSelectedEstudiante(null);
        setFormData({
            id_carrera_destino: '',
            id_mencion_destino: '',
            id_asignacion_actual: '',
            id_asignacion_nueva: '',
            motivo: '',
            tipo_solicitante: 'kardex'
        });
    };

    const selectEstudiante = async (est) => {
        if (activeTab === 'paralelo' && !est.id_asignacion) return; // Prevent selection of students without subjects

        setSelectedEstudiante(est);
        if (activeTab === 'paralelo') {
            // Load available parallels for the selected subject
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/kardex/paralelos-disponibles/${est.id_asignatura}?gestion=${gestion}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                setAvailableParalelos(data.paralelos || []);
                setFormData(prev => ({ ...prev, id_asignacion_actual: est.id_asignacion }));
            } catch (error) {
                console.error('Error loading available paralelos:', error);
            }
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (submitting) return;

        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            let endpoint = '';
            let body = {};

            if (activeTab === 'carrera') {
                endpoint = '/kardex/cambio-carrera/solicitud';
                body = {
                    id_estudiante: selectedEstudiante.id_estudiante,
                    id_carrera_origen: selectedEstudiante.id_carrera,
                    id_mencion_origen: selectedEstudiante.id_mencion,
                    id_carrera_destino: parseInt(formData.id_carrera_destino),
                    id_mencion_destino: formData.id_mencion_destino ? parseInt(formData.id_mencion_destino) : null,
                    motivo: formData.motivo
                };
            } else if (activeTab === 'mencion') {
                endpoint = '/kardex/cambio-mencion/solicitud';
                body = {
                    id_estudiante: selectedEstudiante.id_estudiante,
                    id_carrera: selectedEstudiante.id_carrera,
                    id_mencion_origen: selectedEstudiante.id_mencion,
                    id_mencion_destino: parseInt(formData.id_mencion_destino),
                    motivo: formData.motivo
                };
            } else if (activeTab === 'paralelo') {
                endpoint = '/kardex/cambio-paralelo/solicitud';
                body = {
                    id_estudiante: selectedEstudiante.id_estudiante,
                    id_asignacion_actual: formData.id_asignacion_actual,
                    id_asignacion_nueva: parseInt(formData.id_asignacion_nueva),
                    motivo: formData.motivo,
                    tipo_solicitante: formData.tipo_solicitante
                };
            }

            const response = await fetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            // Handle potential non-JSON errors gracefully
            const contentType = response.headers.get("content-type");
            let result = {};
            if (contentType && contentType.indexOf("application/json") !== -1) {
                result = await response.json();
            } else {
                result = { message: await response.text() };
            }

            if (response.ok) {
                setShowModal(false);
                loadDashboardData();
                alert(`Solicitud enviada exitosamente: ${result.message || ''}`);
            } else {
                alert(result.message || 'Error al enviar solicitud');
            }
        } catch (error) {
            console.error('Error submitting request:', error);
            alert('Error de conexión con el servidor. Por favor verifique su conexión.');
        } finally {
            setSubmitting(false);
        }
    };

    const exportToExcel = (data, fileName) => {
        let headers = [];
        let rows = [];

        if (activeTab === 'carrera') {
            headers = ['Código', 'Nombres', 'Apellidos', 'CI', 'Fecha Solicitud', 'Carrera Inicial', 'Carrera Modificada', 'Estado', 'Motivo Rechazo'];
            rows = data.map(row => [
                row.codigo || row.codigo_estudiante,
                row.nombres,
                `${row.apellido_paterno} ${row.apellido_materno || ''}`,
                row.cedula_identidad,
                new Date(row.fecha_solicitud).toLocaleDateString(),
                row.carrera_origen_nombre,
                row.carrera_destino_nombre,
                row.estado,
                row.observaciones || '-'
            ]);
        } else if (activeTab === 'mencion') {
            headers = ['Código', 'Nombres', 'Apellidos', 'CI', 'Fecha Solicitud', 'Mención Inicial', 'Mención Modificada', 'Estado', 'Motivo Rechazo'];
            rows = data.map(row => [
                row.codigo || row.codigo_estudiante,
                row.nombres,
                `${row.apellido_paterno} ${row.apellido_materno || ''}`,
                row.cedula_identidad,
                new Date(row.fecha_solicitud).toLocaleDateString(),
                row.mencion_origen_nombre || 'Sin Mención',
                row.mencion_destino_nombre,
                row.estado,
                row.observaciones || '-'
            ]);
        } else { // paralelo
            headers = ['Código', 'Nombres', 'Apellidos', 'CI', 'Fecha Solicitud', 'Tipo', 'Asignatura', 'Paralelo Origen', 'Paralelo Destino', 'Estado', 'Motivo Rechazo'];
            rows = data.map(row => [
                row.codigo || row.codigo_estudiante,
                row.nombres,
                `${row.apellido_paterno} ${row.apellido_materno || ''}`,
                row.cedula_identidad,
                new Date(row.fecha_solicitud || row.created_at).toLocaleDateString(),
                row.tipo_solicitante || 'Kardex',
                row.asignatura_nombre,
                row.paralelo_origen,
                row.paralelo_destino,
                row.estado,
                row.observaciones || '-'
            ]);
        }

        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Solicitudes");
        XLSX.writeFile(workbook, `${fileName}_${gestion}.xlsx`);
    };

    const getStatusStyle = (estado) => {
        const e = estado?.toLowerCase();
        if (e === 'aprobado' || e === 'aceptado') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        if (e === 'rechazado') return 'bg-rose-100 text-rose-700 border-rose-200';
        return 'bg-purple-100 text-purple-700 border-purple-200'; // Standardized purple for pending
        // Previous was bg-amber-100
    };

    return (
        <div className="p-6">
            {/* Header with Title */}
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">
                    Kardex &gt; Movimientos Académicos
                </h1>
                {/* <p className="text-slate-500 mt-1">Control de trámites de carrera, mención y paralelos para Kardex.</p> */}
            </div>

            {/* Filters Row: Search + Gestion + Actions */}
            {/* <div className="mb-6 flex flex-wrap items-center gap-3">
                <div className="relative group w-full sm:w-72 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                    <input
                        type="text"
                        placeholder="Buscar..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full bg-transparent border-none py-2.5 pl-11 pr-4 focus:outline-none text-slate-700 font-medium text-sm"
                    />
                </div>

                <button
                    onClick={() => {
                        const data = activeTab === 'carrera' ? carreraSol : activeTab === 'mencion' ? mencionSol : paraleloSol;
                        exportToExcel(data, `Cambio_${activeTab}`);
                    }}
                    className="bg-emerald-50 hover:bg-emerald-100 text-emerald-600 p-2.5 rounded-2xl transition-colors border border-emerald-100 shadow-sm shadow-emerald-50/50"
                    title="Exportar a Excel"
                >
                    <Download size={20} />
                </button>

                <button
                    onClick={handleNewRequest}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-2xl font-bold shadow-lg shadow-indigo-100 transition-all flex items-center justify-center gap-2 whitespace-nowrap text-sm"
                >
                    <RefreshCw size={18} className="animate-[spin_10s_linear_infinite]" />
                    <span>Nueva Solicitud</span>
                </button>
            </div> */}

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">


                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            {/* <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label> */}
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}

                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                const data = activeTab === 'carrera' ? carreraSol : activeTab === 'mencion' ? mencionSol : paraleloSol;
                                exportToExcel(data, `Cambio_${activeTab}`);
                            }}
                            className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                            title="Descargar Excel"
                        >
                            <Download size={20} />
                        </button>

                        <button
                            onClick={handleNewRequest}
                            className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800"
                        >
                            <RefreshCw size={18} className="animate-[spin_10s_linear_infinite]" />
                            <span>Nueva Solicitud</span>
                        </button>

                    </div>

                </div>
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
                {[
                    { id: 'carrera', label: 'Cambio Carrera', icon: Repeat, color: 'bg-blue-900 text-white', bg: 'bg-blue-50' },
                    { id: 'mencion', label: 'Cambio Mención', icon: Layers, color: 'bg-blue-900 text-white', bg: 'bg-blue-50' },
                    { id: 'paralelo', label: 'Cambio Paralelo', icon: UserCheck, color: 'bg-blue-900 text-white', bg: 'bg-blue-50' }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-6 py-2.5 rounded-full font-medium transition flex items-center gap-2 ${activeTab === tab.id
                            ? `${tab.bg} ${tab.color} bg-blue-900 text-white`
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                    >
                        {/* <tab.icon size={18} /> */}
                        {tab.label}
                    </button>
                ))}

            </div>

            {/* Tabs Design */}
            {/* <div className="flex gap-2 mb-6 bg-white p-1.5 rounded-2xl shadow-sm border border-slate-200 w-fit">
                {[
                    { id: 'carrera', label: 'Cambio Carrera', icon: Repeat, color: 'text-blue-600', bg: 'bg-blue-50' },
                    { id: 'mencion', label: 'Cambio Mención', icon: Layers, color: 'text-teal-600', bg: 'bg-teal-50' },
                    { id: 'paralelo', label: 'Cambio Paralelo', icon: UserCheck, color: 'text-violet-600', bg: 'bg-violet-50' }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold transition-all ${activeTab === tab.id
                            ? `${tab.bg} ${tab.color} shadow-sm`
                            : 'text-slate-500 hover:bg-slate-50'
                            }`}
                    >
                        <tab.icon size={18} />
                        {tab.label}
                    </button>
                ))}
            </div> */}

            {/* Dynamic Table Card */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            {activeTab === 'carrera' && (
                                <tr>
                                    <th className="px-3 py-3 text-center font-semibold border">Código</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Estudiante</th>
                                    <th className="px-3 py-3 text-center font-semibold border">CI</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Carrera Inicial</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Carrera Modificada</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Solicitud</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Observaciones</th>
                                </tr>
                            )}
                            {activeTab === 'mencion' && (
                                <tr>
                                    <th className="px-3 py-3 text-center font-semibold border">Código</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Estudiante</th>
                                    <th className="px-3 py-3 text-center font-semibold border">CI</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Mención Inicial</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Mención Modificada</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Solicitud</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Observaciones</th>
                                </tr>
                            )}
                            {activeTab === 'paralelo' && (
                                <tr>
                                    <th className="px-3 py-3 text-center font-semibold border">Código</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Estudiante</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Tipo</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Paralelo Inicial (Info)</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Paralelo Modificado</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Solicitud</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Observaciones</th>
                                </tr>
                            )}
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-20 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                                            <span className="text-slate-400 font-medium">Cargando solicitudes...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                (activeTab === 'carrera' ? carreraSol : activeTab === 'mencion' ? mencionSol : paraleloSol)
                                    .filter(s => {
                                        const str = `${s.codigo_estudiante} ${s.nombres} ${s.apellido_paterno} ${s.cedula_identidad} ${s.asignatura_nombre || ''}`.toLowerCase();
                                        return str.includes(search.toLowerCase());
                                    })
                                    .map((row) => (
                                        <tr key={row.id_solicitud} className="hover:bg-gray-50">
                                            {activeTab === 'carrera' && (
                                                <>
                                                    <td className="px-3 py-2 text-center border">{row.codigo_estudiante}</td>
                                                    <td className="px-3 py-2 border">{row.apellido_paterno} {row.apellido_materno}, {row.nombres}</td>
                                                    <td className="px-3 py-2 border">{row.cedula_identidad}</td>
                                                    <td className="px-3 py-2 border">{row.carrera_origen_nombre}</td>
                                                    <td className="px-3 py-2 border">{row.carrera_destino_nombre}</td>
                                                    <td className="px-3 py-2 border">{new Date(row.fecha_solicitud).toLocaleDateString()}</td>
                                                    <td className="px-3 py-2 border">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(row.estado)}`}>
                                                            {row.estado}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 border">
                                                        <div className={`text-[10px] p-2 rounded-lg border shadow-sm transition-all ${row.estado === 'rechazado' ? 'bg-rose-50 text-rose-700 border-rose-100 font-bold' : 'bg-slate-50 text-slate-500 border-slate-100 italic'}`}>
                                                            {row.observaciones || '-'}
                                                        </div>
                                                    </td>
                                                </>
                                            )}
                                            {activeTab === 'mencion' && (
                                                <>
                                                    <td className="px-3 py-2 border text-center">{row.codigo_estudiante}</td>
                                                    <td className="px-3 py-2 border">{row.apellido_paterno} {row.apellido_materno}, {row.nombres}</td>
                                                    <td className="px-3 py-2 border">{row.cedula_identidad}</td>
                                                    <td className="px-3 py-2 border">{row.mencion_origen_nombre || 'Sin Mención'}</td>
                                                    <td className="px-3 py-2 border">{row.mencion_destino_nombre}</td>
                                                    <td className="px-3 py-2 border">{new Date(row.fecha_solicitud).toLocaleDateString()}</td>
                                                    <td className="px-3 py-2 border">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(row.estado)}`}>
                                                            {row.estado}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 border">
                                                        <div className={`text-[10px] p-2 rounded-lg border shadow-sm transition-all ${row.estado === 'rechazado' ? 'bg-rose-50 text-rose-700 border-rose-100 font-bold' : 'bg-slate-50 text-slate-500 border-slate-100 italic'}`}>
                                                            {row.observaciones || '-'}
                                                        </div>
                                                    </td>
                                                </>
                                            )}
                                            {activeTab === 'paralelo' && (
                                                <>
                                                    <td className="px-3 py-2 border text-center">{row.codigo_estudiante}</td>
                                                    <td className="px-3 py-2 border">{row.apellido_paterno} {row.apellido_materno}, {row.nombres}</td>
                                                    <td className="px-3 py-2 border">{row.tipo_solicitante || 'Kardex'}</td>
                                                    <td className="px-3 py-2 border">{row.asignatura_nombre}</td>
                                                    <td className="px-3 py-2 border">
                                                        <div className="flex flex-col text-slate-500">
                                                            <span className="font-bold text-slate-700 uppercase">Paralelo {row.paralelo_origen}</span>
                                                            <span>{row.docente_origen}</span>
                                                            <span>{row.dias_origen} {row.hora_origen}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 border">
                                                        <div className="flex flex-col text-xs">
                                                            <span className="text-violet-600 font-bold uppercase tracking-wider">Paralelo {row.paralelo_destino}</span>
                                                            <span className="text-slate-400">{row.dias_destino} {row.hora_destino}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 border">
                                                        {new Date(row.fecha_solicitud || row.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-3 py-2 border">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(row.estado)}`}>
                                                            {row.estado}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 border">
                                                        <div className={`text-[10px] p-2 rounded-lg border shadow-sm transition-all ${row.estado === 'rechazado' ? 'bg-rose-50 text-rose-700 border-rose-100 font-bold' : 'bg-slate-50 text-slate-500 border-slate-100 italic'}`}>
                                                            {row.observaciones || '-'}
                                                        </div>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                            )}
                            {!loading && (activeTab === 'carrera' ? carreraSol : activeTab === 'mencion' ? mencionSol : paraleloSol).length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-6 py-20 text-center text-slate-400 italic">No hay solicitudes registradas para esta gestión.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* NEW REQUEST MODAL */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b">

                            <h2 className="text-xl font-bold text-blue-900">Nueva Solicitud: {activeTab === 'carrera' ? 'Cambio de Carrera' : activeTab === 'mencion' ? 'Cambio de Mención' : 'Cambio de Paralelo'}</h2>
                            {/* <p className="text-xs text-blue-100 opacity-80 uppercase tracking-widest font-medium mt-0.5">Gestión {gestion}</p> */}

                            <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                                <X size={26} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* Student Search in Modal */}
                            {!selectedEstudiante ? (
                                <div className="space-y-4">
                                    <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                                        <label className="block text-sm font-semibold mb-2 text-blue-900">Buscar Estudiante Académico</label>
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                            <input
                                                type="text"
                                                placeholder="CI o nombre del estudiante..."
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                className="w-full bg-white border border-slate-200 rounded-lg py-2.5 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm font-medium"
                                            />
                                        </div>
                                        <p className="mt-2 text-[10px] text-blue-600 font-medium">Escriba al menos 3 caracteres para filtrar la lista.</p>
                                    </div>

                                    <div className="flex flex-col gap-2 max-h-[420px] overflow-y-auto pr-2 custom-scrollbar pb-2">
                                        {estudiantes
                                            .map(est => (
                                                <button
                                                    key={`${est.id_estudiante}-${est.id_asignacion || 'no-subject'}`}
                                                    onClick={() => selectEstudiante(est)}
                                                    disabled={activeTab === 'paralelo' && !est.id_asignacion}
                                                    className={`flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl transition-all text-left shadow-sm group ${activeTab === 'paralelo' && !est.id_asignacion
                                                        ? 'opacity-60 cursor-not-allowed grayscale-[0.5]'
                                                        : 'hover:border-blue-300 hover:bg-blue-50/30'
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        {/* Avatar con diseño cuadrado y color dinámico según activeTab */}
                                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs shadow-sm shrink-0 ${activeTab === 'carrera' ? 'bg-indigo-100 text-indigo-700' :
                                                                activeTab === 'mencion' ? 'bg-teal-100 text-teal-700' :
                                                                    'bg-violet-100 text-violet-750 text-violet-700'
                                                            }`}>
                                                            {est.nombres?.[0] || '?'}
                                                        </div>

                                                        <div>
                                                            {/* Nombre con formato Apellido, Nombre y CI integrado */}
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <p className="font-bold text-slate-800 text-xs">
                                                                    {est.apellido_paterno} {est.apellido_materno ? `${est.apellido_materno} ` : ''}, {est.nombres}
                                                                </p>
                                                                <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-mono font-bold uppercase tracking-wider">
                                                                    CI: {est.cedula_identidad}
                                                                </span>
                                                            </div>

                                                            {/* Código del estudiante */}
                                                            <p className="text-[9px] text-slate-400 font-mono mt-0.5">
                                                                CÓDIGO: {est.codigo || est.id_estudiante}
                                                            </p>

                                                            {/* Sección condicional para el tab 'paralelo' con el mismo estilo limpio */}
                                                            {activeTab === 'paralelo' && (
                                                                <div className="flex items-center gap-1.5 mt-1 pt-1 border-t border-slate-100">
                                                                    {est.id_asignacion ? (
                                                                        <>
                                                                            <span className="text-[9px] bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded font-bold uppercase tracking-tight">
                                                                                {est.asignatura_nombre}
                                                                            </span>
                                                                            <span className="text-[9px] font-bold text-slate-500">
                                                                                PARALELO {est.paralelo}
                                                                            </span>
                                                                        </>
                                                                    ) : (
                                                                        <span className="text-[9px] bg-amber-50 text-amber-600 border border-amber-100 px-2 py-0.5 rounded-full font-bold uppercase tracking-widest flex items-center gap-1">
                                                                            <AlertCircle size={10} /> Sin paralelo asignado (2026)
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {!est.id_asignacion && activeTab === 'paralelo' ? (
                                                        <div className="bg-slate-100 text-slate-400 px-3 py-1 rounded text-[10px] font-bold whitespace-nowrap">BLOQUEADO</div>
                                                    ) : (
                                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-blue-900 text-white px-3 py-1 rounded text-[10px] font-bold whitespace-nowrap shadow-md shadow-blue-200">SELECCIONAR</div>
                                                    )}
                                                </button>
                                            ))}
                                        {search.length >= 3 && estudiantes.length === 0 && (
                                            <div className="p-10 text-center text-slate-400 italic bg-slate-50 rounded-xl border border-dashed border-slate-200">
                                                No se encontraron estudiantes que coincidan con la búsqueda.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-300">
                                    {/* Student Card Summary */}
                                    <div className={`p-5 rounded-xl border flex items-center justify-between ${activeTab === 'carrera' ? 'bg-indigo-50/50 border-indigo-100' : activeTab === 'mencion' 
                                        }`}>
                                        <div className="flex items-center gap-4">
                                            <div className={`w-14 h-14 rounded-lg flex items-center justify-center text-white text-xl font-bold shadow-md ${activeTab === 'carrera' ? 'bg-indigo-600' : activeTab === 'mencion' ? 'bg-teal-600' : 'bg-violet-600'
                                                }`}>
                                                {selectedEstudiante.nombres[0]}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-slate-800 leading-tight">{selectedEstudiante.apellido_paterno} {selectedEstudiante.apellido_materno}, {selectedEstudiante.nombres}</h3>
                                                <div className="flex gap-3 mt-1.5">
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-white/60 px-2 py-0.5 rounded border border-slate-100">CI: {selectedEstudiante.cedula_identidad}</span>
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-white/60 px-2 py-0.5 rounded border border-slate-100">COD: {selectedEstudiante.codigo || selectedEstudiante.id_estudiante}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <button onClick={() => setSelectedEstudiante(null)} className="text-[10px] font-bold text-blue-600 bg-white border border-blue-100 px-3 py-1.5 rounded-lg shadow-sm hover:bg-blue-600 hover:text-white transition-all uppercase tracking-widest">Cambiar Selección</button>
                                    </div>

                                    {/* Modal Specific Form Inputs */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* Left Side: Current Context */}
                                        <div className="space-y-4">
                                            <h4 className="text-xs font-bold uppercase border-b pb-2">Información Actual</h4>

                                            {activeTab === 'carrera' && (
                                                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-1">
                                                    <p className="text-xs text-slate-400 font-bold uppercase">Carrera:</p>
                                                    <p className="font-bold text-slate-700">{selectedEstudiante.carrera_nombre || 'Sin Carrera'}</p>
                                                    <p className="text-xs text-slate-400 font-bold uppercase mt-2">Mención:</p>
                                                    <p className="text-slate-600">{selectedEstudiante.mencion_nombre || 'Sin Mención'}</p>
                                                </div>
                                            )}

                                            {activeTab === 'mencion' && (
                                                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3">
                                                    <div>
                                                        <p className="text-xs text-slate-400 font-bold uppercase ">Carrera Seleccionada:</p>
                                                        <p className="font-bold text-slate-700">{selectedEstudiante.carrera_nombre}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-slate-400 font-bold uppercase">Mención Actual:</p>
                                                        <p className="text-slate-600 italic">{selectedEstudiante.mencion_nombre || 'No posee mención'}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {activeTab === 'paralelo' && (
                                                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3">
                                                    <div>
                                                        <p className="text-xs text-slate-400 font-bold uppercase">Asignatura:</p>
                                                        <p className="font-bold text-slate-700">{selectedEstudiante.asignatura_nombre}</p>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <p className="text-xs text-slate-400 font-bold uppercase">Paralelo:</p>
                                                            <p className="text-slate-800 font-bold">{selectedEstudiante.paralelo}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-slate-400 font-bold uppercase">Docente:</p>
                                                            <p className="text-slate-800">{selectedEstudiante.docente_nombre}</p>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-slate-400 font-bold uppercase">Horario:</p>
                                                        <p className="text-slate-600">{selectedEstudiante.dias} {selectedEstudiante.hora_inicio}-{selectedEstudiante.hora_fin}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Right Side: Change Destination */}
                                        <div className="space-y-4">
                                            <h4 className="text-xs font-bold uppercase border-b pb-2">Nueva Configuración</h4>

                                            {activeTab === 'carrera' && (
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">CARRERA DESTINO</label>
                                                        <select
                                                            value={formData.id_carrera_destino}
                                                            onChange={(e) => setFormData({ ...formData, id_carrera_destino: e.target.value, id_mencion_destino: '' })}
                                                            className="w-full bg-white border border-slate-200 p-3 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-400 transition-all font-bold text-slate-700"
                                                        >
                                                            <option value="">Seleccione una carrera</option>
                                                            {carreras.map(c => <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>)}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">MENCIÓN DESTINO (OPCIONAL)</label>
                                                        <select
                                                            value={formData.id_mencion_destino}
                                                            onChange={(e) => setFormData({ ...formData, id_mencion_destino: e.target.value })}
                                                            className="w-full bg-white border border-slate-200 p-3 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-400 transition-all font-bold text-slate-700"
                                                        >
                                                            <option value="">Sin mención / Seleccione</option>
                                                            {menciones.filter(m => m.id_carrera == formData.id_carrera_destino).map(m => (
                                                                <option key={m.id_mencion} value={m.id_mencion}>{m.nombre}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}

                                            {activeTab === 'mencion' && (
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">NUEVA MENCIÓN</label>
                                                    <select
                                                        value={formData.id_mencion_destino}
                                                        onChange={(e) => setFormData({ ...formData, id_mencion_destino: e.target.value })}
                                                        className="w-full bg-white border border-slate-200 p-3 rounded-xl focus:ring-4 focus:ring-teal-100 focus:border-teal-400 transition-all font-bold text-slate-700"
                                                    >
                                                        <option value="">Seleccione la nueva mención</option>
                                                        {menciones
                                                            .filter(m => m.id_carrera == selectedEstudiante.id_carrera && m.id_mencion != selectedEstudiante.id_mencion)
                                                            .map(m => (
                                                                <option key={m.id_mencion} value={m.id_mencion}>{m.nombre}</option>
                                                            ))
                                                        }
                                                    </select>
                                                    {menciones.filter(m => m.id_carrera == selectedEstudiante.id_carrera && m.id_mencion != selectedEstudiante.id_mencion).length === 0 && (
                                                        <p className="text-[10px] text-amber-600 mt-1 font-medium italic">No hay otras menciones disponibles para esta carrera.</p>
                                                    )}
                                                </div>
                                            )}

                                            {activeTab === 'paralelo' && (
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">PARALELO DISPONIBLE</label>
                                                    <select
                                                        value={formData.id_asignacion_nueva}
                                                        onChange={(e) => setFormData({ ...formData, id_asignacion_nueva: e.target.value })}
                                                        className="w-full bg-white border border-slate-200 p-3 rounded-xl focus:ring-4 focus:ring-violet-100 focus:border-violet-400 transition-all font-bold text-slate-700"
                                                    >
                                                        <option value="">Seleccione el nuevo paralelo</option>
                                                        {availableParalelos
                                                            .filter(p => p.id_asignacion != selectedEstudiante.id_asignacion)
                                                            .map(p => (
                                                                <option key={p.id_asignacion} value={p.id_asignacion} disabled={p.cupos_libres <= 0}>
                                                                    Paralelo {p.paralelo} - {p.dias} {p.hora_inicio}
                                                                </option>
                                                            ))
                                                        }
                                                    </select>
                                                    {availableParalelos.filter(p => p.id_asignacion != selectedEstudiante.id_asignacion).length === 0 && (
                                                        <p className="text-[10px] text-amber-600 mt-1 font-medium italic">No hay otros paralelos aprobados disponibles para esta materia.</p>
                                                    )}
                                                    <div className="mt-3 flex items-center gap-2">
                                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Solicitante:</label>
                                                        <select
                                                            value={formData.tipo_solicitante}
                                                            onChange={(e) => setFormData({ ...formData, tipo_solicitante: e.target.value })}
                                                            className="text-xs bg-slate-50 border-none focus:ring-0 font-bold p-0 text-indigo-500 cursor-pointer"
                                                        >
                                                            <option value="kardex">KARDEX</option>
                                                            <option value="estudiante">ESTUDIANTE</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">MOTIVO DE LA SOLICITUD</label>
                                        <textarea
                                            rows={2}
                                            placeholder="Indique brevemente el motivo del cambio..."
                                            value={formData.motivo}
                                            onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 p-3 rounded-2xl focus:ring-4 focus:outline-none focus:ring-indigo-100 focus:border-indigo-400 transition-all placeholder:text-slate-300"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {selectedEstudiante && (
                            <div className="p-6 border-t border-slate-100 flex justify-center bg-slate-50/30">
                                <button
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    className={`px-12 py-3 rounded-full font-bold text-white shadow-lg transition-all flex items-center gap-2 ${submitting ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-900 hover:bg-blue-800'
                                        }`}
                                >
                                    {submitting ? (
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <Send size={18} />
                                    )}
                                    <span>{submitting ? 'Enviando...' : 'Enviar Solicitud a Dirección'}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default KardexCambioCarrera;
